import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useRef
} from 'react'
import * as Mtds from '@mattilsynet/design/react'

type ModalDialogProps = Omit<
  ComponentPropsWithoutRef<typeof Mtds.Dialog>,
  'open'
> & {
  children: ReactNode
  open: boolean
}

export const ModalDialog = ({
  children,
  open,
  onClose,
  closedby = 'any',
  ...props
}: ModalDialogProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const openRef = useRef(open)

  openRef.current = open

  useEffect(() => {
    const dialog = dialogRef.current
    let reopenTimeoutId: number | undefined

    const handleClose = () => {
      reopenTimeoutId = window.setTimeout(() => {
        const currentDialog = dialogRef.current

        if (
          openRef.current &&
          currentDialog &&
          !currentDialog.open &&
          currentDialog.isConnected
        ) {
          try {
            currentDialog.showModal()
          } catch {
            // Ignore cases where the dialog cannot be re-opened.
          }
        }
      })
    }

    dialog?.addEventListener('close', handleClose)

    return () => {
      if (reopenTimeoutId != null) window.clearTimeout(reopenTimeoutId)
      dialog?.removeEventListener('close', handleClose)
    }
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current

    if (!dialog) return

    if (open && !dialog.open) {
      dialog.showModal()
      return
    }

    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <Mtds.Dialog
      ref={dialogRef}
      closedby={closedby}
      onClose={onClose}
      {...props}
    >
      {children}
    </Mtds.Dialog>
  )
}
